import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faCheckCircle, faInfoCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ComponentProps } from 'react'
import { twMerge } from 'tailwind-merge'

export type AlertColor = 'success' | 'warning' | 'error' | 'info'

const alertColors: Record<AlertColor, { background: string; icon: IconProp; iconStyle: string; arrow: string }> = {
  success: {
    icon: faCheckCircle,
    iconStyle: 'text-positive-500',
    background: 'bg-positive-50 border border-positive-500',
    arrow: 'before:border-b-positive-500 after:border-b-positive-50',
  },
  info: {
    icon: faInfoCircle,
    iconStyle: 'text-grey-900 text-lg',
    background: 'bg-grey-100 border border-grey-900',
    arrow: 'before:border-b-info-500 after:border-b-info-50',
  },
  warning: {
    icon: faInfoCircle,
    iconStyle: 'text-warning-500',
    background: 'bg-warning-50 border border-warning-500',
    arrow: 'before:border-b-warning-500 after:border-b-warning-50',
  },
  error: {
    icon: faTimesCircle,
    iconStyle: 'text-negative-500',
    background: 'bg-negative-50 border border-negative-500',
    arrow: 'before:border-b-negative-500 after:border-b-negative-50',
  },
}

interface Props extends ComponentProps<'div'> {
  color: AlertColor
  withArrow?: boolean
  withIcon?: boolean
}

export default function Alert({ children, className, color, withArrow, withIcon, ...props }: Props) {
  const classes = []

  classes.push('flex relative w-fit rounded-lg items-center p-2 text-sm @max-w-[280px] @md:max-w-[357px]')

  classes.push(alertColors[color].background)

  withArrow &&
    classes.push(
      'relative',
      'mt-2',

      'before:block',
      'before:absolute',
      'before:left-3',
      'before:-top-4',
      'before:border-8',
      'before:border-x-transparent',
      'before:border-t-transparent',

      'after:block',
      'after:absolute',
      'after:left-3',
      'after:-top-[15px]',
      'after:border-8',
      'after:border-x-transparent',
      'after:border-t-transparent',
    )
  withArrow && classes.push(alertColors[color].arrow)

  return (
    <div className={twMerge(classes, className)} role="presentation" {...props}>
      {withIcon && (
        <FontAwesomeIcon
          icon={alertColors[color].icon}
          className={`mr-2 mt-0.5 self-start ${alertColors[color].iconStyle}`}
        />
      )}
      <div>{children}</div>
    </div>
  )
}
